import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { GoogleMap } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import { cellToLatLng, cellToParent, getResolution } from 'h3-js';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { API_ENDPOINTS } from '../../config/utilityApiConfig';
import { DailyProfileChart, HexAnalyticsModal, SEASON_COLORS } from './hexForecastCharts';
import InfoTooltip from '../../components/common/InfoTooltip';
import { formatEnergyKwh, formatPowerKw } from '../../utils/formatEnergyUnits';

// ─── Constants ───────────────────────────────────────────────────────────────

const CA_HD_YEARS = [2027, 2028, 2029, 2030];
const DEFAULT_YEAR = 2027;
const CEC_HD_STOCK = {
    2027: 2198,
    2028: 3223,
    2029: 5156,
    2030: 9447,
};

function caseNameForYear(year) {
    return `California Baseline ${year}`;
}
const SIDEBAR_WIDTH = 420;
const CA_MAP_CENTER = { lat: 36.7783, lng: -119.4179 };
const CA_MAP_ZOOM = 6;
const HEX_ZOOM_ON_SELECT = 10;
const NATIVE_HEX_RES = 7;
const HEX_RES_MIN = 4;
const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const MAP_OPTIONS = {
    mapTypeId: 'roadmap',
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControlOptions: { position: 3 },
    styles: [
        { featureType: 'poi',     elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
};

const ENERGY_RAMP_COLORS = [
    { rgba: [136, 19,  55,  235], hex: '#881337' },
    { rgba: [220, 38,  38,  225], hex: '#dc2626' },
    { rgba: [234, 88,  12,  215], hex: '#ea580c' },
    { rgba: [101, 163, 13,  205], hex: '#65a30d' },
    { rgba: [13,  148, 136, 200], hex: '#0d9488' },
    { rgba: [37,  99,  235, 190], hex: '#2563eb' },
    { rgba: [30,  58,  138, 185], hex: '#1e3a8a' },
];
const MIN_THRESHOLD_KWH = 1000;
const EMPTY_HEX_COLOR = [156, 163, 175, 100];


function resolutionForZoom(zoom) {
    const z = Number(zoom);
    if (!Number.isFinite(z) || z < 7) return HEX_RES_MIN;
    if (z < 9) return 5;
    if (z < 10) return 6;
    return NATIVE_HEX_RES;
}

function safeResolution(h3Index) {
    try {
        return getResolution(String(h3Index));
    } catch (_) {
        return NATIVE_HEX_RES;
    }
}

function aggregateHexes(hexes, targetRes) {
    if (!Array.isArray(hexes) || hexes.length === 0) return [];
    const res = Number(targetRes);
    if (!Number.isFinite(res) || res >= NATIVE_HEX_RES) return hexes;
    const byParent = new Map();
    for (const hex of hexes) {
        let parent;
        try {
            parent = cellToParent(String(hex.h3_index), res);
        } catch (_) {
            continue;
        }
        const energy = Number(hex.annual_energy_kwh) || 0;
        const peak = Number(hex.peak_power_kw) || 0;
        const existing = byParent.get(parent);
        if (!existing) {
            byParent.set(parent, {
                h3_index: parent,
                annual_energy_kwh: energy,
                peak_power_kw: peak,
                child_count: 1,
            });
        } else {
            existing.annual_energy_kwh += energy;
            if (peak > existing.peak_power_kw) existing.peak_power_kw = peak;
            existing.child_count += 1;
        }
    }
    return Array.from(byParent.values());
}

function selectionForDisplay(selectedHex, displayedHexes, displayRes) {
    if (!selectedHex?.h3_index) return null;
    const selectedRes = safeResolution(selectedHex.h3_index);
    if (selectedRes === displayRes) {
        return displayedHexes.find((h) => h.h3_index === selectedHex.h3_index) || selectedHex;
    }
    if (selectedRes > displayRes) {
        try {
            const parent = cellToParent(String(selectedHex.h3_index), displayRes);
            return displayedHexes.find((h) => h.h3_index === parent) || { ...selectedHex, h3_index: parent };
        } catch (_) {
            return selectedHex;
        }
    }
    return null;
}

function prettyLogCeiling(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return MIN_THRESHOLD_KWH;
    const exp = Math.floor(Math.log10(n));
    const base = 10 ** exp;
    const f = n / base;
    const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
    return nice * base;
}

function prettyLogSequence(minVal, maxVal, mantissas) {
    const lo = Math.max(minVal, MIN_THRESHOLD_KWH);
    const hi = prettyLogCeiling(Math.max(maxVal, lo));
    const expStart = Math.floor(Math.log10(lo));
    const expEnd = Math.floor(Math.log10(hi));
    const out = [];
    for (let exp = expStart; exp <= expEnd + 1; exp += 1) {
        const scale = 10 ** exp;
        for (const mantissa of mantissas) {
            const value = mantissa * scale;
            if (value >= lo * 0.999 && value <= hi * 1.001) out.push(value);
        }
    }
    const unique = [...new Set(out.map((v) => Number(v.toPrecision(12))))].sort((a, b) => a - b);
    if (!unique.length || unique[0] > lo) unique.unshift(lo);
    if (unique[unique.length - 1] < hi) unique.push(hi);
    return unique;
}

function prettyLogBreaks(minVal, maxVal, targetCount) {
    const wanted = Math.max(2, targetCount);
    let chosen = prettyLogSequence(minVal, maxVal, [1, 2, 5]);
    for (const mantissas of [[1, 2, 5], [1, 5], [1]]) {
        const seq = prettyLogSequence(minVal, maxVal, mantissas);
        chosen = seq;
        if (seq.length <= wanted) break;
    }
    if (chosen.length <= wanted) return chosen;
    const thinned = [];
    const last = chosen.length - 1;
    for (let i = 0; i < wanted; i += 1) {
        thinned.push(chosen[Math.round((i * last) / (wanted - 1))]);
    }
    return [...new Set(thinned)];
}

function rampColorsForCount(count) {
    const n = ENERGY_RAMP_COLORS.length;
    if (count >= n) return ENERGY_RAMP_COLORS.slice(0, count);
    if (count <= 1) return [ENERGY_RAMP_COLORS[n - 1]];
    return Array.from({ length: count }, (_, i) => (
        ENERGY_RAMP_COLORS[Math.round((i * (n - 1)) / (count - 1))]
    ));
}

function buildRelativeEnergyScale(hexes) {
    const values = (hexes || [])
        .map((h) => Number(h.annual_energy_kwh) || 0)
        .filter((v) => v > 0)
        .sort((a, b) => a - b);
    if (!values.length) {
        return {
            maxKwh: 0,
            key: 'empty',
            stops: [],
            legend: [{ label: 'No load', color: '#9ca3af' }],
        };
    }

    const maxV = values[values.length - 1];
    const prettyBreaks = prettyLogBreaks(
        MIN_THRESHOLD_KWH,
        Math.max(maxV, MIN_THRESHOLD_KWH),
        ENERGY_RAMP_COLORS.length - 1
    );
    const cutoffs = [...prettyBreaks].sort((a, b) => b - a);
    if (cutoffs[cutoffs.length - 1] !== MIN_THRESHOLD_KWH) {
        cutoffs.push(MIN_THRESHOLD_KWH);
    }
    cutoffs.push(0);

    const colors = rampColorsForCount(cutoffs.length);
    const stops = cutoffs.map((minKwh, i) => ({
        minKwh,
        color: colors[i].rgba,
        hex: colors[i].hex,
    }));
    const legend = stops.map((stop, i) => {
        const next = i === 0 ? null : stops[i - 1];
        let label;
        if (i === 0) {
            label = `> ${formatEnergyKwh(stop.minKwh)}/yr`;
        } else if (stop.minKwh <= 0) {
            label = `< ${formatEnergyKwh(next.minKwh)}/yr`;
        } else {
            label = `${formatEnergyKwh(stop.minKwh)}–${formatEnergyKwh(next.minKwh)}/yr`;
        }
        return { label, color: stop.hex };
    });
    return {
        maxKwh: maxV,
        key: cutoffs.join(','),
        stops,
        legend,
    };
}

function getHexColor(annualEnergyKwh, scale) {
    const kwh = Number(annualEnergyKwh) || 0;
    if (!scale?.stops?.length || scale.maxKwh <= 0) return EMPTY_HEX_COLOR;
    for (const stop of scale.stops) {
        if (kwh >= stop.minKwh) return stop.color;
    }
    return scale.stops[scale.stops.length - 1].color;
}

function SidebarSectionTitle({ children, className = '', style, tone = 'muted' }) {
    const colorClass = tone === 'black' ? 'text-gray-900' : 'text-gray-400';
    return (
        <p className={`text-[10px] font-semibold uppercase tracking-wider ${colorClass} ${className}`} style={style}>
            {children}
        </p>
    );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function HexMetricTable({ rows }) {
    return (
        <table className="w-full overflow-hidden rounded-md border border-gray-200 tabular-nums">
            <tbody>
                {rows.map((row, idx) => (
                    <tr key={row.label} className={idx < rows.length - 1 ? 'border-b border-gray-100' : ''}>
                        <td className="px-2.5 py-2 text-sm text-gray-600">{row.label}</td>
                        <td className="px-2.5 py-2 text-right">
                            <div className="text-base font-semibold text-gray-900">{row.value}</div>
                            {row.sub ? (
                                <div className="text-[11px] font-normal text-gray-400">{row.sub}</div>
                            ) : null}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function MapTypeToggle({ map }) {
    const [mapType, setMapType] = useState('roadmap');
    if (!map) return null;

    const applyType = (id) => {
        map.setMapTypeId(id);
        setMapType(id);
    };

    const btn = (id, label) => {
        const active = id === 'hybrid'
            ? mapType === 'hybrid' || mapType === 'satellite'
            : mapType === id;
        return (
            <button
                type="button"
                onClick={() => applyType(id)}
                className={`px-3 py-1 ${active ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
            >
                {label}
            </button>
        );
    };

    return (
        <div className="absolute top-2.5 left-2.5 z-20 flex overflow-hidden rounded border border-gray-300 bg-white text-[13px] font-medium shadow">
            {btn('roadmap', 'Map')}
            <span className="w-px self-stretch bg-gray-300" aria-hidden="true" />
            {btn('hybrid', 'Satellite')}
        </div>
    );
}

function YearSlider({ year, onChange, years = CA_HD_YEARS }) {
    const index = Math.max(0, years.indexOf(year));
    const fillPct = years.length > 1 ? (index / (years.length - 1)) * 100 : 0;
    return (
        <div>
            <div className="mb-1 flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Year</span>
                <span className="text-sm font-semibold tabular-nums text-gray-800">{year}</span>
            </div>
            <div className="px-3">
                <div className="relative h-5">
                    <div className="absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200" />
                    <div
                        className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-sky-300"
                        style={{ width: `${fillPct}%` }}
                    />
                    <div
                        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-sky-400 shadow"
                        style={{ left: `${fillPct}%` }}
                    />
                    <input
                        type="range"
                        min={0}
                        max={years.length - 1}
                        step={1}
                        value={index}
                        aria-label="Forecast year"
                        onChange={(e) => onChange(years[Number(e.target.value)])}
                        className="absolute inset-0 w-full cursor-pointer opacity-0"
                    />
                </div>
                <div className="relative mt-0.5 h-4 text-[10px] text-gray-400">
                    {years.map((y, i) => {
                        const left = years.length > 1 ? (i / (years.length - 1)) * 100 : 0;
                        return (
                            <span
                                key={y}
                                className={`absolute -translate-x-1/2 tabular-nums ${y === year ? 'font-semibold text-gray-600' : ''}`}
                                style={{ left: `${left}%` }}
                            >
                                {y}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─── Map Legend (overlay) ─────────────────────────────────────────────────────

function MapLegend({ items = [] }) {
    return (
        <div className="absolute bottom-5 left-3 bg-white border border-gray-200 rounded-lg p-3 shadow text-xs z-10 pointer-events-none">
            <p className="font-semibold text-gray-700 mb-1.5">Annual EV Load</p>
            {items.map(item => (
                <div key={item.label} className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-gray-600">{item.label}</span>
                </div>
            ))}
        </div>
    );
}

// ─── Hex Detail Panel (sidebar) ───────────────────────────────────────────────

function HexDetailPanel({ hexData, onClose, onOpenModal }) {
    if (!hexData) return null;
    const { h3_index, annual_energy_kwh, peak_power_kw, daily_avg_profile_kwh, seasonal_profiles, loading, error } = hexData;
    const resolution = hexData.resolution ?? safeResolution(h3_index);
    const isAggregated = Boolean(hexData.aggregated) || resolution < NATIVE_HEX_RES;
    const annualEnergy = annual_energy_kwh != null ? formatEnergyKwh(annual_energy_kwh) : '—';
    const peakPower = peak_power_kw != null ? formatPowerKw(peak_power_kw) : '—';
    const dailyKwh  = annual_energy_kwh != null
        ? annual_energy_kwh / 365
        : (Array.isArray(daily_avg_profile_kwh)
            ? daily_avg_profile_kwh.reduce((sum, v) => sum + Number(v || 0), 0)
            : null);
    const dailyDisplay = dailyKwh == null ? '—' : formatEnergyKwh(dailyKwh);
    const hasFullProfile = !loading && !error && Array.isArray(hexData.hourly_profile_8760_kwh) && hexData.hourly_profile_8760_kwh.length > 0;
    const analysisLabel = hasFullProfile ? 'View Full Analysis' : 'Loading full profile…';

    return (
        <div className="flex flex-col min-h-0 flex-1">
            <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100">
                <button
                    onClick={onOpenModal}
                    disabled={!hasFullProfile}
                    className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                        hasFullProfile
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    {analysisLabel}
                </button>
            </div>

            <div className="flex-shrink-0 px-4 pt-3 pb-2 border-b border-gray-100 bg-white">
                <SidebarSectionTitle tone="black">Hex summary</SidebarSectionTitle>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0">
                {loading && (
                    <div className="flex items-center justify-center py-10">
                        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-orange-500" />
                    </div>
                )}

                {error && !loading && (
                    <div className="p-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-600">{error}</div>
                    </div>
                )}

                {!loading && !error && (
                    <div className="p-4 space-y-4">
                        <HexMetricTable
                            rows={[
                                { label: 'Annual Energy', value: annualEnergy, sub: isAggregated ? 'sum of hexes' : 'per year' },
                                { label: 'Peak Demand', value: peakPower, sub: isAggregated ? 'coincident peak' : 'hex peak' },
                                { label: 'Daily Energy', value: dailyDisplay, sub: 'average day' },
                            ]}
                        />
                        {isAggregated && (
                            <p className="text-[11px] text-gray-400">
                                Hourly load is the sum of native hexagons inside this cell.
                            </p>
                        )}

                        {Array.isArray(daily_avg_profile_kwh) && daily_avg_profile_kwh.some(v => v > 0) && (
                            <div>
                                <SidebarSectionTitle className="mb-2">Annual Average Hourly Load Profile</SidebarSectionTitle>
                                <div className="bg-gray-50 rounded-lg px-1 py-2">
                                    <DailyProfileChart values={daily_avg_profile_kwh} color="#f97316" />
                                </div>
                            </div>
                        )}

                        {seasonal_profiles && Object.values(seasonal_profiles).some(v => Array.isArray(v) && v.some(x => x > 0)) && (
                            <div>
                                <SidebarSectionTitle className="mb-2">Seasonal Profiles</SidebarSectionTitle>
                                <div className="space-y-3">
                                    {['spring','summer','fall','winter'].map(s => {
                                        const vals = seasonal_profiles[s];
                                        if (!vals || !vals.some(v => v > 0)) return null;
                                        return (
                                            <div key={s}>
                                                <SidebarSectionTitle className="mb-2" style={{ color: SEASON_COLORS[s] }}>
                                                    {s}
                                                </SidebarSectionTitle>
                                                <div className="bg-gray-50 rounded-lg px-1 py-2">
                                                    <DailyProfileChart values={vals} color={SEASON_COLORS[s]} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-start justify-between px-4 py-3 border-t border-gray-100 flex-shrink-0">
                <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">Selected Hexagon</p>
                    <p className="text-sm font-mono font-semibold text-gray-800 break-all leading-tight">{h3_index}</p>
                    <p className="mt-0.5 text-[11px] text-gray-400">
                        H3 res {resolution}
                        {isAggregated && hexData.child_count
                            ? ` · ${hexData.child_count} hex${hexData.child_count === 1 ? '' : 'es'}`
                            : ''}
                    </p>
                </div>
                <button onClick={onClose} className="ml-2 flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5" aria-label="Deselect">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

// ─── Deck.gl H3 overlay ───────────────────────────────────────────────────────

function CaHexOverlay({ map, hexData, onHexClick, selectedHex, displayRes = NATIVE_HEX_RES, energyScale }) {
    const overlayRef = useRef(null);
    const [overlayReady, setOverlayReady] = useState(false);

    const layers = useMemo(() => {
        if (!hexData || !hexData.length) return [];
        const selected = selectedHex?.h3_index ? [selectedHex] : [];
        return [
            new H3HexagonLayer({
                id: `ca-statewide-hex-${displayRes}`,
                data: hexData,
                pickable: true,
                filled: true,
                stroked: true,
                extruded: false,
                highPrecision: true,
                getHexagon: (d) => String(d.h3_index),
                getFillColor: (d) => getHexColor(d.annual_energy_kwh, energyScale),
                getLineColor: [255, 255, 255, 45],
                lineWidthUnits: 'pixels',
                lineWidthMinPixels: 1,
                getLineWidth: 1,
                updateTriggers: {
                    getFillColor: [energyScale?.key, energyScale?.maxKwh, displayRes],
                },
                parameters: { depthTest: false },
                onClick: (info) => { if (info?.object) onHexClick(info.object); },
            }),
            selected.length > 0
                ? new H3HexagonLayer({
                    id: `ca-statewide-hex-selected-${displayRes}`,
                    data: selected,
                    pickable: false,
                    filled: false,
                    stroked: true,
                    extruded: false,
                    highPrecision: true,
                    getHexagon: (d) => String(d.h3_index),
                    getLineColor: [0, 0, 0, 255],
                    lineWidthUnits: 'pixels',
                    lineWidthMinPixels: 4,
                    getLineWidth: 4,
                    parameters: { depthTest: false },
                })
                : null,
        ].filter(Boolean);
    }, [hexData, selectedHex, displayRes, onHexClick, energyScale]);

    useEffect(() => {
        if (!map || overlayRef.current) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            if (cancelled) return;
            try {
                const overlay = new GoogleMapsOverlay({ interleaved: false, pickingRadius: 8 });
                overlay.setMap(map);
                overlayRef.current = overlay;
                setOverlayReady(true);
            } catch (_) {}
        }, 100);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            setOverlayReady(false);
            if (overlayRef.current) {
                try { overlayRef.current.setMap(null); } catch (_) {}
                overlayRef.current = null;
            }
        };
    }, [map]);

    useEffect(() => {
        if (!overlayReady || !overlayRef.current) return;
        overlayRef.current.setProps({ layers });
    }, [layers, overlayReady]);

    return null;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CaStatewiseCasePage() {
    const { isLoaded } = useGoogleMaps();

    const [mapInstance, setMapInstance]   = useState(null);
    const [mapZoom, setMapZoom]           = useState(CA_MAP_ZOOM);
    const mapRef = useRef(null);
    const [caseDetail,  setCaseDetail]    = useState(null);
    const [hexData,     setHexData]       = useState([]);
    const [hexDataStats, setHexDataStats] = useState(null);
    const [loading,     setLoading]       = useState(true);
    const [error,       setError]         = useState(null);

    const [selectedHex,    setSelectedHex]    = useState(null);
    const [hexProfile,     setHexProfile]     = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError,   setProfileError]   = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [year, setYear]           = useState(DEFAULT_YEAR);
    const caseQueryName = caseNameForYear(year);

    const authHeaders = useCallback(() => {
        const token = localStorage.getItem('authToken');
        return token ? { Authorization: `Token ${token}` } : {};
    }, []);

    // ── Fetch case + hex data ──────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        async function fetchAll() {
            setLoading(true); setError(null);
            try {
                const [detailRes, hexRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.caStatewiseCase, {
                        params: { case_name: caseQueryName },
                        headers: authHeaders(),
                    }),
                    axios.get(API_ENDPOINTS.caStatewiseCaseHexData, {
                        params: { case_name: caseQueryName, year, peak: 'coincident' },
                        headers: authHeaders(),
                    }),
                ]);
                if (cancelled) return;
                setCaseDetail(detailRes.data);
                const raw = hexRes.data;
                setHexDataStats({
                    hexCount: raw.hex_count,
                    totalMwh: raw.total_annual_energy_mwh,
                    peakKw: raw.peak_power_kw,
                    peakHourLabel: raw.peak_hour_label || null,
                    peakHourIndex: raw.peak_hour_index ?? null,
                });
                setHexData(raw.hex_data || []);
            } catch (err) {
                if (!cancelled) {
                    setHexData([]);
                    setHexDataStats(null);
                    setCaseDetail(null);
                    setError(err?.response?.data?.detail || `The ${year} heavy-duty forecast is not available yet.`);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchAll();
        return () => { cancelled = true; };
    }, [year, caseQueryName, authHeaders]);

    // ── Select hex (click or search) ──────────────────────────────────────────
    const selectHex = useCallback(async (hexObj) => {
        const h3_index = hexObj?.h3_index;
        if (!h3_index) return;
        const resolution = hexObj.resolution ?? safeResolution(h3_index);
        const isAggregated = resolution < NATIVE_HEX_RES;

        setSelectedHex({ ...hexObj, resolution });
        setModalOpen(false);
        setProfileError(null);

        if (mapInstance) {
            try {
                const [lat, lng] = cellToLatLng(h3_index);
                mapInstance.panTo({ lat, lng });
                if (!isAggregated && mapInstance.getZoom() < HEX_ZOOM_ON_SELECT) {
                    mapInstance.setZoom(HEX_ZOOM_ON_SELECT);
                }
            } catch (_) {}
        }

        setHexProfile({
            h3_index,
            annual_energy_kwh: hexObj.annual_energy_kwh,
            peak_power_kw: hexObj.peak_power_kw,
            loading: true,
            aggregated: isAggregated,
            child_count: hexObj.child_count,
            resolution,
        });
        setProfileLoading(true);

        try {
            const res = await axios.get(
                API_ENDPOINTS.caStatewiseCaseHexProfile,
                { params: { case_name: caseQueryName, h3_index, year }, headers: authHeaders() }
            );
            setHexProfile({
                ...res.data,
                loading: false,
                resolution: res.data.resolution ?? resolution,
                aggregated: Boolean(res.data.aggregated) || isAggregated,
                child_count: res.data.child_count ?? hexObj.child_count,
            });
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not load hex profile.';
            setHexProfile(prev => ({ ...prev, loading: false, error: msg, resolution, aggregated: isAggregated }));
            setProfileError(msg);
        } finally {
            setProfileLoading(false);
        }
    }, [year, caseQueryName, authHeaders, mapInstance]);

    const handleClosePanel  = useCallback(() => { setSelectedHex(null); setHexProfile(null); setModalOpen(false); }, []);
    const handleOpenModal   = useCallback(() => setModalOpen(true),  []);
    const handleCloseModal  = useCallback(() => setModalOpen(false), []);
    const handleMapLoad = useCallback((map) => {
        mapRef.current = map;
        setMapInstance(map);
        setMapZoom(map.getZoom() ?? CA_MAP_ZOOM);
    }, []);
    const handleZoomChanged = useCallback(() => {
        const z = mapRef.current?.getZoom();
        if (z != null) setMapZoom(z);
    }, []);

    // ── Derived stats ──────────────────────────────────────────────────────────
    const simTotals  = caseDetail?.simulation_totals || {};
    const truckCount = caseDetail?.input_summary?.target_ev_count ?? CEC_HD_STOCK[year];

    const annualLoadKwh = hexDataStats?.totalMwh != null
        ? Number(hexDataStats.totalMwh) * 1000
        : (simTotals?.total_mwh_per_day != null ? Number(simTotals.total_mwh_per_day) * 365 * 1000 : null);
    const annualLoadValue = annualLoadKwh != null && Number.isFinite(annualLoadKwh)
        ? formatEnergyKwh(annualLoadKwh)
        : '—';
    const dailyLoadValue = (() => {
        if (hexDataStats?.totalMwh != null && Number.isFinite(Number(hexDataStats.totalMwh))) {
            return formatEnergyKwh(Number(hexDataStats.totalMwh) * 1000 / 365, { suffix: '/day' });
        }
        if (simTotals?.total_mwh_per_day != null && Number.isFinite(Number(simTotals.total_mwh_per_day))) {
            return formatEnergyKwh(Number(simTotals.total_mwh_per_day) * 1000, { suffix: '/day' });
        }
        return null;
    })();
    const peakDemandValue = hexDataStats?.peakKw != null
        ? formatPowerKw(hexDataStats.peakKw)
        : '—';
    const truckCountValue = truckCount != null ? Number(truckCount).toLocaleString() : '—';
    const peakHourLabel = hexDataStats?.peakHourLabel || null;
    const displayRes = resolutionForZoom(mapZoom);
    const displayedHexData = useMemo(
        () => aggregateHexes(hexData, displayRes),
        [hexData, displayRes]
    );
    const energyScale = useMemo(
        () => buildRelativeEnergyScale(displayedHexData),
        [displayedHexData]
    );
    const displayedSelection = useMemo(
        () => selectionForDisplay(selectedHex, displayedHexData, displayRes),
        [selectedHex, displayedHexData, displayRes]
    );

    const handleYearChange = useCallback((nextYear) => {
        if (nextYear === year) return;
        setYear(nextYear);
        setSelectedHex(null);
        setHexProfile(null);
        setModalOpen(false);
        setProfileError(null);
    }, [year]);

    return (
        <div className="flex h-full min-h-0 overflow-hidden bg-gray-50">

            {/* Map — full height of the content area */}
            <div className="relative flex-1 min-w-0 min-h-0">
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
                        <div className="flex flex-col items-center gap-3">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
                            <p className="text-sm text-gray-500 font-medium">Loading California hex data…</p>
                        </div>
                    </div>
                )}
                {error && !loading && (
                    <div className="absolute inset-0 flex items-center justify-center z-20">
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-sm text-center shadow">
                            <p className="text-red-700 font-semibold mb-1">
                                {year === DEFAULT_YEAR ? 'Failed to load case' : `${year} forecast not ready`}
                            </p>
                            <p className="text-sm text-red-500">{error}</p>
                            {year !== DEFAULT_YEAR && (
                                <button
                                    type="button"
                                    onClick={() => handleYearChange(DEFAULT_YEAR)}
                                    className="mt-3 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600"
                                >
                                    View 2027
                                </button>
                            )}
                            <div className="mt-3 text-left">
                                <YearSlider year={year} onChange={handleYearChange} />
                            </div>
                        </div>
                    </div>
                )}
                <div className="absolute inset-0">
                    {isLoaded && (
                        <GoogleMap
                            mapContainerStyle={MAP_CONTAINER_STYLE}
                            center={CA_MAP_CENTER}
                            zoom={CA_MAP_ZOOM}
                            options={MAP_OPTIONS}
                            onLoad={handleMapLoad}
                            onZoomChanged={handleZoomChanged}
                            onIdle={handleZoomChanged}
                        >
                            {mapInstance && (
                                <CaHexOverlay
                                    map={mapInstance}
                                    hexData={displayedHexData}
                                    onHexClick={selectHex}
                                    selectedHex={displayedSelection}
                                    displayRes={displayRes}
                                    energyScale={energyScale}
                                />
                            )}
                        </GoogleMap>
                    )}
                </div>
                <MapTypeToggle map={mapInstance} />
                <MapLegend items={energyScale.legend} />
                {!loading && hexData.length > 0 && !selectedHex && (
                    <div className="absolute bottom-5 right-3 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow text-xs text-gray-500 z-10 pointer-events-none">
                        {displayRes < NATIVE_HEX_RES
                            ? 'Zoom in to see finer hexagons, or click one for its charging profile'
                            : 'Click a hexagon to view its charging profile'}
                    </div>
                )}
            </div>

            {/* Sidebar */}
            <div className="flex-shrink-0 bg-white border-l border-gray-200 flex flex-col min-h-0" style={{ width: SIDEBAR_WIDTH }}>
                <div className="flex-shrink-0 space-y-3 border-b border-gray-100 px-4 py-3">
                    <YearSlider year={year} onChange={handleYearChange} />
                    <div className="space-y-2">
                        <SidebarSectionTitle tone="black">Statewide summary</SidebarSectionTitle>
                        <table className="w-full overflow-hidden rounded-md border border-gray-200 tabular-nums">
                            <tbody>
                                <tr className="border-b border-gray-100">
                                    <td className="px-2.5 py-2 text-sm text-gray-600">Total Annual Load</td>
                                    <td className="px-2.5 py-2 text-right">
                                        <div className="text-base font-semibold text-gray-900">{annualLoadValue}</div>
                                        {dailyLoadValue && (
                                            <div className="text-[11px] font-normal text-gray-400">{dailyLoadValue}</div>
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-b border-gray-100">
                                    <td className="px-2.5 py-2 text-sm text-gray-600">Peak Demand</td>
                                    <td className="px-2.5 py-2 text-right">
                                        <div className="text-base font-semibold text-gray-900">{peakDemandValue}</div>
                                        {peakHourLabel && (
                                            <div className="text-[11px] font-normal text-gray-400 leading-snug">{peakHourLabel}</div>
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-2.5 py-2 text-sm text-gray-600">
                                        <span className="inline-flex items-center gap-1">
                                            Heavy-duty truck stock
                                            <InfoTooltip
                                                label="About heavy-duty truck stock"
                                                align="full"
                                            >
                                                This count is the California Energy Commission (CEC) Class 7–8
                                                heavy-duty electric truck stock projection for {year}. The map
                                                load is scaled to this statewide fleet size.
                                            </InfoTooltip>
                                        </span>
                                    </td>
                                    <td className="px-2.5 py-2 text-right">
                                        <div className="text-base font-semibold text-gray-900">{truckCountValue}</div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {selectedHex && (
                    <HexDetailPanel
                        hexData={hexProfile || {
                            h3_index: selectedHex.h3_index,
                            annual_energy_kwh: selectedHex.annual_energy_kwh,
                            peak_power_kw: selectedHex.peak_power_kw,
                            loading: profileLoading,
                            error: profileError,
                            aggregated: (selectedHex.resolution ?? safeResolution(selectedHex.h3_index)) < NATIVE_HEX_RES,
                            child_count: selectedHex.child_count,
                            resolution: selectedHex.resolution ?? safeResolution(selectedHex.h3_index),
                        }}
                        onClose={handleClosePanel}
                        onOpenModal={handleOpenModal}
                    />
                )}

                {!selectedHex && (
                    <div className="flex-1 overflow-y-auto min-h-0 p-4">
                        <p className="text-xs text-gray-400 italic">
                            Select a hexagon on the map to view its charging profile.
                        </p>
                    </div>
                )}
            </div>

            {/* Full analysis modal */}
            <HexAnalyticsModal
                open={modalOpen}
                onClose={handleCloseModal}
                h3_index={selectedHex?.h3_index}
                profile={hexProfile}
                subtitle={`Hex Profile Analysis · CA ${year} MDHD`}
            />
        </div>
    );
}
